import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPrint, faExpand, faCompress, faUpRightFromSquare, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import Modal from './Modal';
import type { TimEInventoryRow } from '../data/timEInventoryList';
import './MonthlyInventoryReportModal.css';

function parsePartNumber(s: string): number {
  return Number(String(s).replace(/[$,]/g, '').trim()) || 0;
}

function formatUsd(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function escapeCsvCell(v: string): string {
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

function escapeHtml(v: string): string {
  return String(v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildCsv(headers: string[], rows: string[][]): string {
  const lines = [headers.map(escapeCsvCell).join(',')];
  for (const row of rows) {
    lines.push(row.map(escapeCsvCell).join(','));
  }
  return lines.join('\r\n');
}

/**
 * Column headers from the March 2026 inventory spreadsheet row 1.
 * Column C uses the report month (e.g. "April #") so it stays aligned with the report title.
 */
function getReportHeaders(monthName: string): string[] {
  return [
    'Product', // A
    'Deployed', // B
    `Available (${monthName})`, // C
    'Replacement Cost per item', // D
    'Value', // E
    'Discarded Units', // F
    'Waste $', // G
  ];
}

/** Row shown when a spreadsheet line item has no matching TIM-E parts DB row. */
const EMPTY_COUNTS_ROW: TimEInventoryRow = {
  product: '',
  pendingSales: '0',
  available: '0',
  fullyAssembled: '0',
  notAssembled: '0',
  backOrdered: '0',
  replacementCostPerItem: '',
  value: '',
  discardedUnits: '0',
  wasteDollars: '',
};

function normProductKey(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s+/g, ' ');
}

/** Match spreadsheet column A labels to DB `product` (handles minor naming differences). */
function timEProductNamesMatch(canonicalA: string, dbProduct: string): boolean {
  const a = normProductKey(canonicalA);
  const b = normProductKey(dbProduct);
  if (a === b) return true;
  if (a.startsWith(b) || b.startsWith(a)) return true;
  return false;
}

/**
 * Active catalog products a report row stands for. Rows are named after catalog products, so
 * names must match exactly: timEProductNamesMatch ignores parentheticals and would pair
 * "Insulated Food Transport" with "Insulated Food Transport (Powered)".
 */
function activeCatalogProductsForRow<T extends { name: string; catalogStatus?: string }>(
  catalog: T[],
  rowProduct: string
): T[] {
  return catalog.filter(
    (p) => (p.catalogStatus ?? 'Active') !== 'Void' && String(p.name ?? '').trim() === rowProduct
  );
}

/**
 * Replacement cost comes from the row's catalog product (`inventory_products`) — the source of
 * truth the user maintains. It overrides any legacy TIM-E parts value; the TIM-E parts value is
 * only kept when the catalog has no cost set.
 */
function applyCatalogReplacementCostToRows(
  rows: TimEInventoryRow[],
  catalog: Array<{ name: string; replacementCostPerItem?: string; catalogStatus?: string }> | undefined
): TimEInventoryRow[] {
  if (!catalog?.length) return rows;
  return rows.map((r) => {
    const catalogCost = activeCatalogProductsForRow(catalog, r.product)
      .map((p) => String(p.replacementCostPerItem ?? '').trim())
      .find(Boolean);
    if (!catalogCost) return r;
    return { ...r, replacementCostPerItem: catalogCost };
  });
}

function inventoryUnitPlacementQty(u: { stockQuantity?: number }): number {
  return u.stockQuantity != null && Number(u.stockQuantity) > 0 ? Math.floor(Number(u.stockQuantity)) : 1;
}

/**
 * For rows that map to an Active catalog product, Deployed and Available come from the
 * `inventory_units` of every Active product with the row's name: Deployed = units in use
 * (status Deployed or Event); Available = units that are actually available (status In Storage
 * or missing/legacy — i.e. not Deployed, Event, Repair, Out of Order, or In-Route).
 * TIM-E-only rows (no catalog match) keep TIM-E values.
 */
function applyInventoryUnitCountsToReportColumns(
  rows: TimEInventoryRow[],
  catalog: Array<{ id?: string; name: string; catalogStatus?: string }> | undefined,
  units: Array<{ productId: string; stockQuantity?: number; status?: string; assembled?: boolean }> | undefined
): TimEInventoryRow[] {
  if (!catalog?.length || units === undefined) return rows;

  const UNAVAILABLE_STATUSES = new Set(['Deployed', 'Event', 'Repair', 'Out of Order', 'In-Route']);

  return rows.map((r) => {
    const productIds = new Set(
      activeCatalogProductsForRow(catalog, r.product)
        .map((p) => String(p.id ?? '').trim())
        .filter(Boolean)
    );
    if (productIds.size === 0) return r;

    let inField = 0;
    let available = 0;

    for (const u of units) {
      if (!productIds.has(u.productId)) continue;
      const qty = inventoryUnitPlacementQty(u);
      if (u.status === 'Deployed' || u.status === 'Event') inField += qty;
      if (!UNAVAILABLE_STATUSES.has(String(u.status ?? ''))) available += qty;
    }

    return {
      ...r,
      pendingSales: String(inField),
      available: String(available),
    };
  });
}

function catalogColumnOrder(
  catalog: Array<{ name: string; catalogStatus?: string }> | undefined
): string[] | null {
  if (!catalog?.length) return null;
  const names = catalog
    .filter((p) => String(p.name ?? '').trim() !== '' && (p.catalogStatus ?? 'Active') !== 'Void')
    .map((p) => String(p.name).trim());
  const unique = Array.from(new Set(names));
  if (unique.length === 0) return null;
  unique.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return unique;
}

/**
 * One row per Active inventory catalog product (A–Z). TIM-E parts only fill F–J
 * where a part name matches a catalog line. No fallback to the legacy template
 * and no extra rows for TIM-E parts that aren't in the inventory catalog —
 * "what's in inventory" is the single source of truth for column A.
 */
function buildMergedReportRows(
  parts: TimEInventoryRow[],
  catalogProducts?: Array<{ name: string; catalogStatus?: string }>
): TimEInventoryRow[] {
  const columnOrder = catalogColumnOrder(catalogProducts);
  if (!columnOrder?.length) return [];

  const usable = parts.filter((r) => String(r.product ?? '').trim() !== '');
  const matched = new Set<number>();
  const out: TimEInventoryRow[] = [];

  for (const name of columnOrder) {
    const idx = usable.findIndex((p, i) => !matched.has(i) && timEProductNamesMatch(name, p.product));
    if (idx !== -1) {
      matched.add(idx);
      const p = usable[idx];
      out.push({ ...p, product: name });
    } else {
      out.push({ ...EMPTY_COUNTS_ROW, product: name });
    }
  }

  return out;
}

/**
 * Value (column E) = (Deployed + Available) × Replacement cost per item, computed
 * after counts and replacement cost have been populated. When there's no
 * replacement cost the value is blank rather than $0 so missing cost data is visible.
 */
function applyComputedValueToRows(rows: TimEInventoryRow[]): TimEInventoryRow[] {
  return rows.map((r) => {
    const cost = parsePartNumber(r.replacementCostPerItem);
    if (cost <= 0) return { ...r, value: '' };
    const qty = parsePartNumber(r.pendingSales) + parsePartNumber(r.available);
    return { ...r, value: formatUsd(qty * cost) };
  });
}

/** Count-style columns: show 0 when empty so zero inventory is visible. */
function displayCount(v: string, displayCell: (v: string) => string): string {
  const d = displayCell(v);
  return d === '' ? '0' : d;
}

function rowToDisplayCells(r: TimEInventoryRow, displayCell: (v: string) => string): string[] {
  return [
    displayCell(r.product),
    displayCount(r.pendingSales, displayCell),
    displayCount(r.available, displayCell),
    displayCell(r.replacementCostPerItem),
    displayCell(r.value),
    displayCount(r.discardedUnits, displayCell),
    displayCell(r.wasteDollars),
  ];
}

export interface MonthlyInventoryReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  timEParts: TimEInventoryRow[];
  /** Catalog products: used to fill column G when the TIM-E parts row has no replacement cost but names match. */
  catalogProducts?: Array<{ id?: string; name: string; type?: string; replacementCostPerItem?: string; catalogStatus?: string }>;
  /** Inventory units: columns B–E for catalog-matched rows (deployed / month total / assembled yes / assembled no). */
  inventoryUnits?: Array<{ productId: string; stockQuantity?: number; status?: string; assembled?: boolean }>;
  reportDate?: Date;
}

/**
 * Full grid from spreadsheet row 1. Column A is driven exclusively by Active inventory
 * catalog product names (sorted A–Z). TIM-E parts rows fill column-F–J counts where
 * names match. Products not in the inventory catalog are excluded.
 */
const MonthlyInventoryReportModal: React.FC<MonthlyInventoryReportModalProps> = ({
  isOpen,
  onClose,
  timEParts,
  catalogProducts,
  inventoryUnits,
  reportDate = new Date(),
}) => {
  const monthName = useMemo(
    () => new Intl.DateTimeFormat('en-US', { month: 'long' }).format(reportDate),
    [reportDate]
  );
  const year = reportDate.getFullYear();
  const reportTitle = `${monthName} ${year} Inventory`;

  const headers = useMemo(() => getReportHeaders(monthName), [monthName]);

  const dataRows = useMemo(() => {
    const merged = buildMergedReportRows(timEParts, catalogProducts);
    const withCost = applyCatalogReplacementCostToRows(merged, catalogProducts);
    const withCounts = applyInventoryUnitCountsToReportColumns(withCost, catalogProducts, inventoryUnits);
    return applyComputedValueToRows(withCounts);
  }, [timEParts, catalogProducts, inventoryUnits]);

  const totals = useMemo(() => {
    let value = 0;
    let discarded = 0;
    let waste = 0;
    for (const r of dataRows) {
      value += parsePartNumber(r.value);
      discarded += parsePartNumber(r.discardedUnits);
      waste += parsePartNumber(r.wasteDollars);
    }
    return { value, discarded, waste };
  }, [dataRows]);

  const displayCell = useCallback((v: string) => (String(v ?? '').trim() === '' ? '' : String(v)), []);

  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const toggleSort = useCallback(
    (col: number) => {
      if (sortCol === col) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortCol(col);
        setSortDir('asc');
      }
    },
    [sortCol]
  );

  const sortedRows = useMemo(() => {
    if (sortCol === null) return dataRows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...dataRows].sort((a, b) => {
      const ca = rowToDisplayCells(a, displayCell)[sortCol];
      const cb = rowToDisplayCells(b, displayCell)[sortCol];
      const na = parsePartNumber(ca);
      const nb = parsePartNumber(cb);
      const aNum = ca.trim() !== '' && !Number.isNaN(Number(String(ca).replace(/[$,]/g, '').trim()));
      const bNum = cb.trim() !== '' && !Number.isNaN(Number(String(cb).replace(/[$,]/g, '').trim()));
      const cmp = aNum && bNum ? na - nb : ca.localeCompare(cb, undefined, { numeric: true, sensitivity: 'base' });
      return cmp * dir;
    });
  }, [dataRows, sortCol, sortDir, displayCell]);

  const [fullScreen, setFullScreen] = useState(false);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!downloadMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target as Node)) {
        setDownloadMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDownloadMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [downloadMenuOpen]);

  const handleClose = useCallback(() => {
    setFullScreen(false);
    setDownloadMenuOpen(false);
    setSortCol(null);
    setSortDir('asc');
    onClose();
  }, [onClose]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const buildReportHtml = useCallback(() => {
    const headRow = headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('');
    const bodyRows = dataRows
      .map((r) => {
        const cells = rowToDisplayCells(r, displayCell)
          .map((cell) => `<td>${escapeHtml(cell)}</td>`)
          .join('');
        return `<tr>${cells}</tr>`;
      })
      .join('');
    const totalRow =
      `<tr class="total-row"><td colspan="4">Total</td>` +
      `<td>${totals.value ? escapeHtml(formatUsd(totals.value)) : ''}</td>` +
      `<td>${totals.discarded ? String(Math.round(totals.discarded)) : ''}</td>` +
      `<td>${totals.waste ? escapeHtml(formatUsd(totals.waste)) : ''}</td></tr>`;

    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtml(reportTitle)}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 24px; color: #1f2933; }
  h1 { font-size: 1.5rem; margin: 0 0 1rem; }
  table { border-collapse: collapse; width: 100%; font-size: 0.85rem; }
  th, td { border: 1px solid #d2d6dc; padding: 6px 8px; text-align: left; }
  thead th { background: #f5f7fa; position: sticky; top: 0; cursor: pointer; user-select: none; white-space: nowrap; }
  thead th:hover { background: #e4e8ec; }
  thead th .sort-ind { font-size: 0.75em; color: #2c7a3f; margin-left: 2px; }
  tfoot .total-row td { font-weight: 700; background: #f5f7fa; }
  @media print { body { margin: 0; } thead th { cursor: default; } }
</style>
</head>
<body>
<h1>${escapeHtml(reportTitle)}</h1>
<table>
<thead><tr>${headRow}</tr></thead>
<tbody>${bodyRows}</tbody>
<tfoot>${totalRow}</tfoot>
</table>
<script>
(function () {
  var table = document.querySelector('table');
  if (!table || !table.tHead || !table.tBodies[0]) return;
  var tbody = table.tBodies[0];
  var headers = table.tHead.rows[0].cells;
  var state = { col: -1, dir: 1 };
  function num(s) { var n = parseFloat(String(s).replace(/[$,]/g, '')); return isNaN(n) ? null : n; }
  function cellText(row, i) { return row.cells[i] ? row.cells[i].textContent.trim() : ''; }
  for (var i = 0; i < headers.length; i++) {
    (function (idx) {
      var th = headers[idx];
      var ind = document.createElement('span');
      ind.className = 'sort-ind';
      th.appendChild(ind);
      th.addEventListener('click', function () {
        if (state.col === idx) { state.dir = -state.dir; } else { state.col = idx; state.dir = 1; }
        var rows = Array.prototype.slice.call(tbody.rows);
        rows.sort(function (a, b) {
          var va = cellText(a, idx), vb = cellText(b, idx);
          var na = num(va), nb = num(vb), cmp;
          if (na !== null && nb !== null) { cmp = na - nb; }
          else { cmp = va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' }); }
          return cmp * state.dir;
        });
        rows.forEach(function (r) { tbody.appendChild(r); });
        for (var h = 0; h < headers.length; h++) {
          var s = headers[h].querySelector('.sort-ind');
          if (s) s.textContent = '';
        }
        ind.textContent = state.dir > 0 ? ' \u25B2' : ' \u25BC';
      });
    })(i);
  }
})();
</script>
</body>
</html>`;
  }, [dataRows, displayCell, headers, reportTitle, totals.discarded, totals.value, totals.waste]);

  const handleOpenInNewTab = useCallback(() => {
    const html = buildReportHtml();
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) {
      URL.revokeObjectURL(url);
      alert('Please allow pop-ups to open the report in a new tab.');
      return;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }, [buildReportHtml]);

  const handleDownloadCsv = useCallback(
    (mode: 'static' | 'formulas') => {
      let bodyRows: string[][];
      let totalRow: string[];

      if (mode === 'formulas') {
        /**
         * Live spreadsheet formulas so numbers recalculate in Excel/Sheets. Deployed (B),
         * Available (C), Replacement cost (D) are plain numbers; Value (E) = (B+C)*D per row;
         * the Total row sums E/F/G. Header is spreadsheet row 1, so data row i is row i + 2.
         */
        bodyRows = dataRows.map((r, i) => {
          const rowNum = i + 2;
          const cost = parsePartNumber(r.replacementCostPerItem);
          const deployed = parsePartNumber(r.pendingSales);
          const available = parsePartNumber(r.available);
          const discarded = parsePartNumber(r.discardedUnits);
          const waste = parsePartNumber(r.wasteDollars);
          return [
            displayCell(r.product),
            String(deployed),
            String(available),
            cost > 0 ? String(cost) : '',
            cost > 0 ? `=(B${rowNum}+C${rowNum})*D${rowNum}` : '',
            String(discarded),
            waste > 0 ? String(waste) : '',
          ];
        });
        const hasRows = dataRows.length > 0;
        const firstRow = 2;
        const lastRow = dataRows.length + 1;
        totalRow = [
          'Total',
          '',
          '',
          '',
          hasRows ? `=SUM(E${firstRow}:E${lastRow})` : '',
          hasRows ? `=SUM(F${firstRow}:F${lastRow})` : '',
          hasRows ? `=SUM(G${firstRow}:G${lastRow})` : '',
        ];
      } else {
        /** Static values exactly as shown in the on-screen table. */
        bodyRows = dataRows.map((r) => rowToDisplayCells(r, displayCell));
        totalRow = [
          'Total',
          '',
          '',
          '',
          totals.value ? String(Math.round(totals.value)) : '',
          totals.discarded ? String(Math.round(totals.discarded)) : '',
          totals.waste ? String(Math.round(totals.waste)) : '',
        ];
      }

      const csv = buildCsv(headers, [...bodyRows, totalRow]);
      const safeFile = `${monthName}-${year}-Inventory`.replace(/[/\\?%*:|"<>]/g, '-');
      const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeFile}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    [dataRows, displayCell, headers, monthName, year, totals.value, totals.discarded, totals.waste]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={reportTitle}
      extraWide={!fullScreen}
      fullScreen={fullScreen}
      contentClassName="inventory-monthly-report-print-scope"
    >
      <div className="modal-body inventory-monthly-report-body">
        <p className="inventory-monthly-report-lead">
          Column A lists only <strong>Active products</strong> from your inventory catalog (sorted A–Z).{' '}
          <strong>Deployed</strong> is the total quantity of units in use (status Deployed or Event);{' '}
          <strong>Available ({monthName})</strong> is the number of units currently available
          (In Storage — excludes Deployed, Event, Repair, Out of Order, and In-Route).{' '}
          Replacement cost comes from the catalog product. Value = (Deployed + Available) × replacement cost.
        </p>
        <div className="inventory-report-toolbar no-print">
          <button type="button" className="page-toolbar-btn" onClick={handlePrint}>
            <FontAwesomeIcon icon={faPrint} style={{ marginRight: '0.35rem' }} aria-hidden />
            Print
          </button>
          <div className="inventory-report-download" ref={downloadMenuRef}>
            <button
              type="button"
              className="page-toolbar-btn"
              onClick={() => setDownloadMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={downloadMenuOpen}
            >
              <FontAwesomeIcon icon={faDownload} style={{ marginRight: '0.35rem' }} aria-hidden />
              Download
              <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: '0.4rem', fontSize: '0.75em' }} aria-hidden />
            </button>
            {downloadMenuOpen && (
              <div className="inventory-report-download-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="inventory-report-download-item"
                  onClick={() => {
                    setDownloadMenuOpen(false);
                    handleDownloadCsv('static');
                  }}
                >
                  <span className="inventory-report-download-item-title">Static values</span>
                  <span className="inventory-report-download-item-sub">Fixed numbers as shown</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="inventory-report-download-item"
                  onClick={() => {
                    setDownloadMenuOpen(false);
                    handleDownloadCsv('formulas');
                  }}
                >
                  <span className="inventory-report-download-item-title">Formulas</span>
                  <span className="inventory-report-download-item-sub">Live spreadsheet formulas</span>
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="page-toolbar-btn"
            onClick={() => setFullScreen((v) => !v)}
            aria-pressed={fullScreen}
          >
            <FontAwesomeIcon icon={fullScreen ? faCompress : faExpand} style={{ marginRight: '0.35rem' }} aria-hidden />
            {fullScreen ? 'Exit full screen' : 'Full screen'}
          </button>
          <button type="button" className="page-toolbar-btn" onClick={handleOpenInNewTab}>
            <FontAwesomeIcon icon={faUpRightFromSquare} style={{ marginRight: '0.35rem' }} aria-hidden />
            Open in new tab
          </button>
        </div>
        <div className="inventory-monthly-report-table-wrap">
          <table className="inventory-monthly-report-table">
            <thead>
              <tr>
                {headers.map((h, idx) => (
                  <th
                    key={h}
                    className="inventory-monthly-report-sortable-th"
                    onClick={() => toggleSort(idx)}
                    aria-sort={sortCol === idx ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    {h}
                    <span className="inventory-monthly-report-sort-indicator" aria-hidden>
                      {sortCol === idx ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r, i) => (
                <tr key={`${r.product}-${i}`}>
                  {rowToDisplayCells(r, displayCell).map((cell, j) => (
                    <td key={j}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="inventory-monthly-report-total-row">
                <td colSpan={4}>Total</td>
                <td>{totals.value ? formatUsd(totals.value) : ''}</td>
                <td>{totals.discarded ? String(Math.round(totals.discarded)) : ''}</td>
                <td>{totals.waste ? formatUsd(totals.waste) : ''}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <div className="modal-actions no-print">
        <button type="button" className="cancel-button" onClick={handleClose}>
          Close
        </button>
      </div>
    </Modal>
  );
};

export default MonthlyInventoryReportModal;
